import React, { useEffect, useState } from 'react';
import { AddPropertySetModalButtonProps, AddPropertySetModalProps, PropertySetCardProps } from './types';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Search from '@mui/icons-material/Search';
import { Button, Modal, ModalBody, ModalHeader } from '@/ui';
import { usePropertySets, usePropertySetTags } from '../../../../views/CustomProperties/PropertySets/queries';

/** A card in a picker list: the whole card is the click target. */
export const CARD_PICKER_SX = {
  cursor: 'pointer',
  borderRadius: 1,
  transition: 'box-shadow 0.2s ease',
  '&:hover': { boxShadow: 6 },
} as const;

const AddPropertySetModal = ({
  isOpen,
  setIsOpen,
  onAddPropertySet,
  alreadyAddedPropertySets,
  isDetailsScreen = false,
}: AddPropertySetModalProps) => {
  const toggle = () => setIsOpen(!isOpen);

  const { data: propertySets, isLoading: loadingPropertySets } = usePropertySets();
  const { data: propertySetTags, isLoading: loadingPropertySetTags } = usePropertySetTags();

  const [propertySetTagFilter, setPropertySetTagFilter] = useState('');
  const [propertySetTextFilter, setPropertySetTextFilter] = useState('');

  const filteredPropertySets = propertySets?.filter((propertySet) => {
    const isAlreadyAdded = alreadyAddedPropertySets.some((addedPropertySet) => addedPropertySet.id === propertySet.id);
    const containsPropertySet =
      isDetailsScreen &&
      propertySet.setProperties.propertySets.length > 0 &&
      propertySet.setProperties.propertySets.some((id) =>
        alreadyAddedPropertySets.some((addedPropertySet) => addedPropertySet.id === id)
      );
    const matchesTag =
      !propertySetTagFilter ||
      propertySetTagFilter === 'All' ||
      propertySet.tags?.some((tag) => tag.name === propertySetTagFilter);
    const matchesText =
      !propertySetTextFilter ||
      propertySet.name?.toLowerCase().includes(propertySetTextFilter.toLowerCase()) ||
      propertySet.description?.toLowerCase().includes(propertySetTextFilter.toLowerCase());
    const isCustomProperty = isDetailsScreen || !!propertySet.additionalConfiguration.isCustomProperty;
    return matchesTag && matchesText && !isAlreadyAdded && !containsPropertySet && isCustomProperty;
  });

  useEffect(() => {
    if (!isOpen) {
      setPropertySetTagFilter('All');
      setPropertySetTextFilter('');
    }
  }, []);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size='lg' tone='success'>
      <ModalHeader toggle={toggle}>Add Property Set</ModalHeader>
      <ModalBody>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 5 }}>
            <FormLabel htmlFor='property-set-tag-filter' sx={{ display: 'block', mb: 0.5 }}>
              PropertySet Tag
            </FormLabel>
            <Autocomplete
              id='property-set-tag-filter'
              options={(propertySetTags && ['All', ...propertySetTags.map((tag) => tag.name)]) || []}
              value={propertySetTagFilter || null}
              onChange={(_event, next) => setPropertySetTagFilter(next ?? '')}
              loading={loadingPropertySetTags}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 7 }}>
            <FormLabel htmlFor='property-set-name-filter' sx={{ display: 'block', mb: 0.5 }}>
              PropertySet Name
            </FormLabel>
            <TextField
              id='property-set-name-filter'
              value={propertySetTextFilter}
              onChange={(e) => setPropertySetTextFilter(e.target.value)}
              disabled={loadingPropertySets}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search fontSize='small' />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Property Sets that already contain a nested Property Set cannot be added to a Property Set.
            </Typography>
          </Grid>
        </Grid>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {filteredPropertySets?.map((propertySet) => (
            <Box key={propertySet.id} onClick={() => onAddPropertySet(propertySet)} sx={CARD_PICKER_SX}>
              <PropertySetCard propertySet={propertySet} />
            </Box>
          ))}
        </Stack>
      </ModalBody>
    </Modal>
  );
};

export const PropertySetCard = ({ propertySet }: PropertySetCardProps) => {
  return (
    <Card>
      <CardHeader
        title={
          <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>{propertySet.name}</Box>
            {propertySet.tags?.map((tag) => (
              <Chip key={tag.name} size='small' color='secondary' label={`#${tag.name}`} />
            ))}
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {propertySet.description}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const AddPropertySetModalButton = ({
  onAddPropertySet,
  alreadyAddedPropertySets,
  isDetailsScreen,
  buttonLabel,
}: AddPropertySetModalButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button color='success' onClick={() => setIsOpen(true)} startIcon={<Add />}>
        {buttonLabel || 'Add Property Set'}
      </Button>
      <AddPropertySetModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onAddPropertySet={onAddPropertySet}
        alreadyAddedPropertySets={alreadyAddedPropertySets}
        isDetailsScreen={!!isDetailsScreen}
      />
    </>
  );
};

export default AddPropertySetModal;
